import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import type { SocialProviderId } from '@/lib/auth';
import { useSocialProviders } from '@/hooks/useSocialProviders';

/**
 * The social sign-in buttons shared by Login and Signup.
 *
 * Only renders providers the backend says are usable — see useSocialProviders.
 * The two pages differ only in which i18n sub-namespace they read ("Continue
 * with Google" vs the sign-up copy) and in what they do before bouncing to the
 * provider, which is why `onProvider` is a callback rather than a hardcoded
 * store call: Login stashes a redirect, Signup also persists a referral code.
 */

interface SocialLoginButtonsProps {
  /** Chooses the i18n sub-namespace: `login.withGoogle` vs `signup.withGoogle`. */
  namespace: 'login' | 'signup';
  onProvider: (provider: SocialProviderId) => void;
  disabled?: boolean;
  /** Rendered under the buttons when at least one is shown. */
  divider?: React.ReactNode;
}

const ICONS: Record<SocialProviderId, React.ReactNode> = {
  google: (
    <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  ),
  apple: (
    <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"
        className="fill-ink"
      />
    </svg>
  ),
  facebook: (
    <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
        fill="#1877F2"
      />
    </svg>
  ),
};

const LABEL_KEY: Record<SocialProviderId, string> = {
  google: 'withGoogle',
  apple: 'withApple',
  facebook: 'withFacebook',
};

export default function SocialLoginButtons({
  namespace,
  onProvider,
  disabled = false,
  divider,
}: SocialLoginButtonsProps) {
  const { t } = useTranslation('auth');
  const { providers } = useSocialProviders();

  if (providers.length === 0) return null;

  return (
    <>
      {providers.map((provider, index) => (
        <button
          key={provider}
          type="button"
          onClick={() => onProvider(provider)}
          disabled={disabled}
          className={cn(
            'flex w-full items-center justify-center gap-3 rounded-lg border border-line-strong bg-surface px-4 py-2.5 text-sm font-medium text-ink-soft',
            'transition-colors hover:bg-surface-soft active:bg-surface-muted',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 focus-visible:ring-offset-2',
            'disabled:opacity-50 disabled:pointer-events-none',
            index > 0 && 'mt-3',
          )}
        >
          {ICONS[provider]}
          {t(`${namespace}.${LABEL_KEY[provider]}`)}
        </button>
      ))}
      {divider}
    </>
  );
}
